import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCampaigns, createCampaign, deleteCampaign, getSmtpConfigs, getTemplates, getContactTags, getWaConfigs, getWaTemplates } from '../api';
import TagsInput from '../components/TagsInput';
import toast from 'react-hot-toast';
import { Send, Plus, Trash2, Eye, Clock, CheckCircle, AlertCircle, MessageSquare, X, Mail, Phone } from 'lucide-react';

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [show, setShow] = useState(false);
  const [smtps, setSmtps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [waConfigs, setWaConfigs] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const nav = useNavigate();
  const [form, setForm] = useState({ name:'', type:'email', templateId:'', smtpConfigId:'', waTemplateId:'', waConfigId:'', contactTags:[], delayBetweenEmails:3000 });

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [c, s, t, tg] = await Promise.all([getCampaigns(), getSmtpConfigs(), getTemplates(), getContactTags()]);
      setCampaigns(c.data); setSmtps(s.data); setTemplates(t.data); setTags(tg.data);
      try { const wc = await getWaConfigs(); setWaConfigs(wc.data); } catch(e){}
      try { const wt = await getWaTemplates(); setWaTemplates(wt.data); } catch(e){}
    } catch(e){}
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error('Campaign name required');
    if (form.contactTags.length===0) return toast.error('Select at least one tag');
    if (form.type === 'email') {
      if (!form.templateId) return toast.error('Select email template');
      if (!form.smtpConfigId) return toast.error('Select SMTP account');
    } else {
      if (!form.waTemplateId) return toast.error('Select WhatsApp template');
      if (!form.waConfigId) return toast.error('Select WhatsApp account');
    }
    try {
      const r = await createCampaign(form);
      toast.success('Campaign created!');
      nav(`/campaigns/${r.data._id}`);
    } catch(e){ toast.error(e.response?.data?.error||'Failed'); }
  };

  return (<>
    <div className="pg-hdr">
      <h2>Campaigns</h2>
      <p>Create and manage email & WhatsApp campaigns</p>
      <div className="pg-acts"><button className="btn btn-p" onClick={()=>setShow(true)}><Plus size={15}/> New Campaign</button></div>
    </div>
    <div className="pg-body">
      {campaigns.length===0 ? <div className="card"><div className="empty"><Send size={44}/><h3>No campaigns</h3><p>Create your first campaign</p></div></div> : (
        <div style={{display:'grid',gap:10}}>{campaigns.map(c=>(
          <Link to={`/campaigns/${c._id}`} key={c._id} style={{textDecoration:'none',color:'inherit'}}>
            <div className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                  <span style={{fontSize:14,marginRight:2}}>{c.type==='whatsapp'?'📱':'📧'}</span>
                  <span style={{fontWeight:600,fontSize:14}}>{c.name}</span>
                  <span className={`badge b-${c.status}`}>{c.status}</span>
                  {c.type==='whatsapp'&&<span className="badge" style={{background:'rgba(37,211,102,.15)',color:'#25D366'}}>WhatsApp</span>}
                </div>
                <div style={{fontSize:11.5,color:'var(--txtm)'}}>
                  Template: {c.templateId?.name||c.waTemplateId?.name||'—'} • {new Date(c.createdAt).toLocaleDateString()}
                </div>
                {c.stats?.total>0&&<div style={{display:'flex',gap:14,marginTop:8}}>
                  <Stat icon={<Clock size={11}/>} l="Pending" v={c.stats.pending} c="var(--warn)"/>
                  <Stat icon={<CheckCircle size={11}/>} l="Delivered" v={c.stats.delivered} c="var(--ok)"/>
                  <Stat icon={<AlertCircle size={11}/>} l="Failed" v={c.stats.failed} c="var(--err)"/>
                  {(c.stats.bounced||0)>0&&<Stat icon={<AlertCircle size={11}/>} l="Bounced" v={c.stats.bounced} c="var(--err)"/>}
                  <Stat icon={<MessageSquare size={11}/>} l="Replied" v={c.stats.replied} c="var(--reply)"/>
                </div>}
              </div>
              <div style={{display:'flex',gap:5}}>
                <button className="btn btn-g btn-sm"><Eye size={13}/></button>
                <button className="btn btn-d btn-sm" onClick={async e=>{e.preventDefault();e.stopPropagation();if(!window.confirm('Delete?'))return;await deleteCampaign(c._id);toast.success('Deleted');load();}}><Trash2 size={13}/></button>
              </div>
            </div>
          </Link>
        ))}</div>
      )}
    </div>

    {show&&<div className="modal-bg" onClick={()=>setShow(false)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Create Campaign</h3><button className="btn btn-g btn-sm" onClick={()=>setShow(false)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Campaign Type</label>
          <div style={{display:'flex',gap:8}}>
            <button className={`btn ${form.type==='email'?'btn-p':'btn-s'}`} onClick={()=>setForm({...form,type:'email'})}><Mail size={14}/> Email</button>
            <button className={`btn ${form.type==='whatsapp'?'btn-p':'btn-s'}`} style={form.type==='whatsapp'?{background:'#25D366',borderColor:'#25D366'}:{}} onClick={()=>setForm({...form,type:'whatsapp'})}><Phone size={14}/> WhatsApp</button>
          </div>
        </div>
        <div className="fg"><label className="lbl">Campaign Name *</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Q1 Outreach"/></div>

        {form.type==='email' ? <div className="fr2">
          <div className="fg"><label className="lbl">Email Template *</label>
            <select className="inp sel" value={form.templateId} onChange={e=>setForm({...form,templateId:e.target.value})}>
              <option value="">Select template...</option>
              {templates.map(t=><option key={t._id} value={t._id}>{t.name} — {t.subject}</option>)}
            </select>
          </div>
          <div className="fg"><label className="lbl">SMTP Account *</label>
            <select className="inp sel" value={form.smtpConfigId} onChange={e=>setForm({...form,smtpConfigId:e.target.value})}>
              <option value="">Select SMTP...</option>
              {smtps.map(s=><option key={s._id} value={s._id}>{s.name} ({s.fromEmail})</option>)}
            </select>
          </div>
        </div> : <div className="fr2">
          <div className="fg"><label className="lbl">WhatsApp Template *</label>
            <select className="inp sel" value={form.waTemplateId} onChange={e=>setForm({...form,waTemplateId:e.target.value})}>
              <option value="">Select template...</option>
              {waTemplates.map(t=><option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          </div>
          <div className="fg"><label className="lbl">WhatsApp Account *</label>
            <select className="inp sel" value={form.waConfigId} onChange={e=>setForm({...form,waConfigId:e.target.value})}>
              <option value="">Select account...</option>
              {waConfigs.map(w=><option key={w._id} value={w._id}>{w.name} ({w.phoneNumber})</option>)}
            </select>
          </div>
        </div>}

        {form.type==='whatsapp'&&<div style={{padding:10,background:'var(--bg3)',borderRadius:8,fontSize:11.5,color:'var(--txt2)',marginBottom:14}}>📱 Contacts without phone numbers will be skipped.</div>}

        <div className="fg">
          <label className="lbl">Target contacts by tags *</label>
          <TagsInput tags={form.contactTags} onChange={ct=>setForm({...form,contactTags:ct})} placeholder="Type or click below"/>
          {tags.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:6}}>
            {tags.map(t=><button key={t} className={`btn btn-sm ${form.contactTags.includes(t)?'btn-p':'btn-s'}`} style={{padding:'3px 10px',fontSize:11}} onClick={()=>setForm({...form,contactTags:form.contactTags.includes(t)?form.contactTags.filter(x=>x!==t):[...form.contactTags,t]})}>{t}</button>)}
          </div>}
        </div>
        <div className="fg">
          <label className="lbl">Delay between messages (ms)</label>
          <input className="inp" type="number" value={form.delayBetweenEmails} onChange={e=>setForm({...form,delayBetweenEmails:+e.target.value})}/>
          <div style={{fontSize:10.5,color:'var(--txtm)',marginTop:3}}>Recommended: 3000-5000ms</div>
        </div>
      </div>
      <div className="modal-ft">
        <button className="btn btn-s" onClick={()=>setShow(false)}>Cancel</button>
        <button className="btn btn-p" onClick={handleCreate}><Plus size={15}/> Create {form.type==='email'?'Email':'WhatsApp'} Campaign</button>
      </div>
    </div></div>}
  </>);
}

function Stat({icon,l,v,c}) {
  return <span style={{display:'flex',alignItems:'center',gap:4,fontSize:11.5}}><span style={{color:c}}>{icon}</span><span style={{color:'var(--txtm)'}}>{l}:</span><span style={{fontWeight:600,fontFamily:'var(--mono)',color:c}}>{v}</span></span>;
}
